import { useCallback, useContext, useRef } from 'react'
import { AuthContext } from 'react-oauth2-code-pkce'
import { useTranslation } from 'react-i18next'

import { storeTokens } from '$api/tokenManagement'
import User from './user'
import Company from './company'

export function useUser() {
  const authContext = useContext(AuthContext)

  const { token, tokenData, idTokenData, idToken } = authContext
  if (!token || !idTokenData) {
    return false
  }

  storeTokens(idToken, token, null)

  const userData = {
    ...idTokenData,
    clientUrls: idTokenData.client_urls,
    companyChain: idTokenData.CompanyChain,
    themeUrl: tokenData.theme_url,
  }

  try {
    return new User(userData, tokenData.realm_access.roles)
  } catch (e) {
    return { error: e }
  }
}

export function useCompany() {
  const authContext = useContext(AuthContext)

  const { token, idTokenData } = authContext
  if (!token || !idTokenData) {
    return false
  }

  return new Company(idTokenData)
}

export function useToken() {
  const { token, idToken, error, loginInProgress, idTokenData, tokenData } =
    useContext(AuthContext)

  const tokenNotYetDecoded = token && !tokenData
  const idTokenNotYetDecoded = idToken && !idTokenData

  return {
    token,
    idToken,
    error,
    loginInProgress:
      loginInProgress || tokenNotYetDecoded || idTokenNotYetDecoded,
  }
}

export function useLogin() {
  // eslint-disable-next-line id-denylist -- wrapper for AuthContext's logIn(), which should be avoided in all other locations
  const { logIn } = useContext(AuthContext)
  const { i18n } = useTranslation()

  return () => {
    logIn(undefined, {
      ui_locales: getUiLocales(i18n.language),
    })
  }
}

export function useLogout() {
  const { logOut } = useContext(AuthContext)
  const { i18n } = useTranslation()

  return () => {
    logOut(undefined, undefined, {
      ui_locales: getUiLocales(i18n.language),
    })
  }
}

function getUiLocales(currentLocale, defaultLocale = 'en') {
  return [...new Set([currentLocale, defaultLocale])].join(' ')
}

// Uses refs so the returned function always reads the most recent token
export function useFreshToken() {
  const { token, idToken } = useContext(AuthContext)
  const tokenRef = useRef(token)
  const idTokenRef = useRef(idToken)

  // Keep refs in sync with the latest context values on every render
  tokenRef.current = token
  idTokenRef.current = idToken

  return useCallback(() => {
    if (tokenRef.current) {
      storeTokens(idTokenRef.current, tokenRef.current, null)
    }
  }, [])
}
